<template class="customer-service-ratings">
  <div>
    <GenericChart
      :request-status="requestStatus"
      :title="title"
      :items="items"
      :map-keys="mapKeys"
      :export-xlsx="true"
      :item-id="itemId"
      :open-filter="openFilter"
      :open-dialog="openDialog"
      :has-custom-filter="true"
      class="make-a-copy"
      v-bind="$attrs"
      @reload="getData"
    />

    <v-data-table
      :v-if="requestStatus=='success'"
      :headers="headers"
      :items="items"
      :items-per-page="itemsPerPage"
      :loading="inputLoading"
      class="table-truncate"
    >
      <template v-slot:[`item.surveyScore`]="{ item }">
        <DisplayRating
          :value="item.survey_score"
          :survey-type="item.survey_type"
        />
      </template>

      <template v-slot:[`item.seeRatings`]="{ item }">
        <DsButton
          v-tooltip="$t('components.CustomerServiceRatings.label.viewRating')"
          color="tertiary"
          name="view"
          :icon="true"
          @click="openCard({ pid: item.pid })"
        />
      </template>
    </v-data-table>

    <v-dialog
      v-model="postDialog"
      fullscreen
    >
      <v-card>
        <v-toolbar>
          <v-toolbar-title>{{ this.$t('components.OperatorsLogs.details') }}</v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <StIcon
              class="close-icon"
              icon-name="close"
              @click="closeCard()"
            />
          </v-toolbar-items>
        </v-toolbar>
        <v-card-actions>
          <PostThemeAndSentiment :post="post" />
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="filterDialog"
      width="50%"
      overlay-color="var(--ds-color-reverse)"
    >
      <v-card>
        <v-toolbar>
          <v-toolbar-title>
            {{ $t('components.filter.ModalFilters.label.sacStatus.title') }}
          </v-toolbar-title>
          <v-spacer />
          <v-toolbar-items class="customer-service-ratings__close-icon">
            <span>
              <StIcon
                class="close-icon"
                icon-name="close"
                @click="filterDialog = false"
              />
            </span>
          </v-toolbar-items>
        </v-toolbar>

        <v-row
          no-gutters
          class="mt-5 mx-5"
        >
          <v-col
            class="pr-4"
            @scroll.stop
          >
            <MultiSelectFilter
              v-model="selectedOperators"
              class="mr-3"
              :select-items="getRepliers"
              item-value="id"
              :label="$t('components.CustomerServiceRatings.label.replierName')"
              :searchable="true"
            />
          </v-col>

          <v-col
            class="pr-4"
            @scroll.stop
          >
            <RatingFilter
              :value="ratings"
              survey-type="csat"
            />
          </v-col>
        </v-row>

        <v-row
          no-gutters
          class="pb-5 px-5"
        >
          <v-spacer />
          <v-spacer />
          <v-btn
            v-if="hasCustomFilterApplied"
            outlined
            rounded
            class="mr-3"
            @click="resetFilter"
          >
            {{ $t('components.CustomerServiceRatings.label.button.clear') }}
          </v-btn>
          <v-btn
            outlined
            rounded
            @click="applyFilter"
          >
            {{ $t('components.CustomerServiceRatings.label.button.apply') }}
          </v-btn>
        </v-row>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="dialog"
      width="70vw"
      height="80vh"
    >
      <v-card>
        <v-card-actions class="customer-service-ratings__close-button">
          <DsButton
            v-tooltip="closeLabel"
            color="tertiary"
            name="close"
            :icon="true"
            @click="dialog = false"
          />
        </v-card-actions>
        <v-card-text class="customer-service-ratings__modal-style">
          <h3>{{ this.$t('components.CustomerServiceRatings.faq.title') }}</h3>
          <p>{{ this.$t('components.CustomerServiceRatings.faq.description') }}</p>

          <h3>{{ this.$t('components.CustomerServiceRatings.faq.faq01.title') }}</h3>
          <p>{{ this.$t('components.CustomerServiceRatings.faq.faq01.description') }}</p>

          <h3>{{ this.$t('components.CustomerServiceRatings.faq.faq02.title') }}</h3>
          <p>{{ this.$t('components.CustomerServiceRatings.faq.faq02.description') }}</p>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import GenericChart from '@/components/cards/models/GenericChart';
import DisplayRating from '@/components/DisplayRating';
import MultiSelectFilter from '@/components/filter/MultiSelectFilter.vue';
import RatingFilter from '@/components/filter/RatingFilter.vue';
import { Flags } from '@/plugins/rollout';
import { DsButton } from '@ds/components';
import { mapActions, mapGetters, mapState } from 'vuex';
import repliersModel from '@/mixins/repliers-model';
import EventBus from '@/utils/event-bus';
import WarRoomService from '@/services/WarRoomService';
import { convertTimeToBrazilianFormatDate } from '@/utils/date';
import sacTypeModel from '@/mixins/sac-type-model';
import PostThemeAndSentiment from '@/components/Post/PostThemeAndSentiment';
import channelModel from '@/mixins/channel-model';

export default {
  name: 'CustomerServiceRatings',
  components: {
    GenericChart,
    DisplayRating,
    MultiSelectFilter,
    DsButton,
    PostThemeAndSentiment,
    RatingFilter,
  },
  mixins: [
    repliersModel,
    sacTypeModel,
    channelModel,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      headers: [
        { text: this.$t('components.CustomerServiceRatings.label.table.header.userName'), value: 'username' },
        { text: this.$t('components.CustomerServiceRatings.label.table.header.surveyScore'), value: 'surveyScore' },
        { text: this.$t('components.CustomerServiceRatings.label.table.header.surveyComment'), value: 'survey_comment' },
        { text: this.$t('components.CustomerServiceRatings.label.table.header.replierName'), value: 'replier_name' },
        { text: this.$t('components.CustomerServiceRatings.label.table.header.responseAt'), value: 'response_at' },
        { text: this.$t('components.OperatorsLogs.viewPost'), value: 'seeRatings', sortable: false },
      ],
      ratings: [],
      items: [],
      selectedOperators: null,
      filterDialog: false,
      hasCustomFilterApplied: false,
      globalFilterByDataTypeToShow: {},
      dialog: false,
      itemsPerPage: 5,
      requestStatus: 'loading',
      postDialog: false,
      post: null,
      closeLabel: this.$t('components.OperatorsLogs.closeLabel'),
      ratingFilterReset: false,
      mapKeys: {
        username: this.$t('components.CustomerServiceRatings.label.table.header.userName'),
        survey_score: this.$t('components.CustomerServiceRatings.label.table.header.surveyScore'),
        survey_comment: this.$t('components.CustomerServiceRatings.label.table.header.surveyComment'),
        replier_name: this.$t('components.CustomerServiceRatings.label.table.header.replierName'),
        response_at: this.$t('components.CustomerServiceRatings.label.table.header.responseAt'),
      },
    };
  },
  computed: {
    ...mapGetters('featureFlag', [
      'isEnabled',
    ]),
    ...mapGetters('dashboard', [
      'activeDashboardFilters',
      'activeDashboard',
      'getCardByItemId',
    ]),
    ...mapGetters('filter', [
      'formattedDateRange',
    ]),
    ...mapState('filter', [
      'accountId',
      'universeId',
    ]),
    ...mapState('dashboard', [
      'isStaging',
    ]),
    showGlobalFilterFeature() {
      return this.isEnabled(Flags.showGlobalFilter) && this.isEditor;
    },
    card() {
      return this.getCardByItemId(this.itemId);
    },
    getUniqueRepliers() {
      return this.items.forEach((rating => {
        rating.id;
        rating.name;
      }), []);
    },
    inputLoading() {
      return this.requestStatus === 'loading';
    },
    getRepliers() {
      return this.repliersToSelectItem(this.activeDashboardFilters?.repliers);
    },
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getSeStudioEndpointService',
      'getWarroomService',
    ]),
    async getFilterId() {
      const sacRepliers = this.selectedOperators ? this.selectedOperators.value : null;
      const newCardFilter = {
        sac_replier: {
          value: sacRepliers,
        },
        is_sac: true,
      };
      return WarRoomService.encodeFilter({
        account_id: this.accountId,
        is_staging: this.isStaging,
        filter: JSON.stringify(newCardFilter),
      });
    },
    getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }
      this.applyFilter(options);
    },
    async applyFilter(options) {
      this.requestStatus = 'loading';

      this.items = [];
      this.componentFilter = await this.getFilterId();

      this.getSeStudioEndpointService({
        itemId: this.itemId,
        servicePath: 'sacSurveys',
        serviceParams: {
          filterId: this.componentFilter,
          ratings: this.ratings,
        },
      })
        .then((response) => {
          this.items = response.data.map(survey => {
            survey.response_at = this.formatDate(survey.response_at);
            return survey;
          });

          this.requestStatus = 'success';
          if (options?.silentRefresh) {
            EventBus.$emit('updated', { itemId: this.itemId });
          }
        })
        .catch(error => {
          console.error(error);
          this.requestStatus = 'error';
        });
      if (this.ratings.length || this.selectedOperators) {
        this.hasCustomFilterApplied = true;
      }
      this.filterDialog = false;
    },
    resetFilter() {
      this.selectedOperators = null;
      this.ratings = [];
      this.applyFilter();
    },
    openFilter() {
      this.filterDialog = true;
    },
    openDialog() {
      this.dialog = true;
    },
    formatDate(date) {
      if (!date) {
        return;
      }
      return convertTimeToBrazilianFormatDate(date);
    },
    async openCard({ pid }) {
      const defaultParams = {
        date_range: this.formattedDateRange,
        account_id: this.accountId,
        universe_id: this.universeId,
        is_staging: this.isStaging,
        limit: 1,
        filter: {
          pids: {
            value: [pid],
          },
          is_sac: true,
          pages: {
            value: this.activeDashboard.data.pages.map(page => page.id),
          },
          channels: {
            value: Object.values(this.channelsList),
          },
        },
      };
      WarRoomService.topPosts({ params: defaultParams })
        .then(res => {
          if (!res.posts[0]) {
            this.postDialog = false;
          } else {
            this.postDialog = true;
            this.post = res.posts[0];
          }
        }).catch(err => console.error(err));
    },
    closeCard() {
      this.postDialog = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.customer-service-ratings {
  &__close-icon {
    align-items: center;
  }

  &__close-button {
    display: flex;
    flex: end;
    justify-content: flex-end;
  }
}

::v-deep {
  .customer-service-ratings {
    &__table-truncate {
      td {
        max-width: 10vw;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    &__modal-style {
      * {
        color: var(--ds-color-snow) !important;
      }

      h3 {
        font-weight: var(--ds-font-weight-bold);
      }
    }

    &__v-dialog {
      overflow-x: hidden;
    }
  }
}
</style>
